// Checkers ↔ the shared board-game views (../board). Multi-jumps are picked
// hop by hop: tap the piece, then each landing square; the move is sent once
// the path is a complete legal move.

import React from 'react';
import { legalMoves, resultText, SEAT_NAMES, type CheckersState, type CMove } from '../../../../supabase/functions/games/checkers.ts';
import type { BoardGameUI, PickRules, PieceSpec } from '../board/boardGame';
import { CheckerPiece } from './CheckerPieces';

const legalCache = new Map<string, CMove[]>();
function legalOf(board: string, side: 0 | 1): CMove[] {
  const k = `${board}${side}`;
  let l = legalCache.get(k);
  if (!l) {
    l = legalMoves(board, side);
    if (legalCache.size > 64) legalCache.clear();
    legalCache.set(k, l);
  }
  return l;
}

const pieceCache = new Map<string, PieceSpec[]>();
function piecesOf(board: string): PieceSpec[] {
  let out = pieceCache.get(board);
  if (out) return out;
  out = [];
  for (let s = 0; s < 64; s++) {
    const ch = board[s];
    if (ch === '.') continue;
    out.push({ sq: s, kind: ch === 'B' || ch === 'W' ? 'king' : 'man', side: ch === 'b' || ch === 'B' ? 0 : 1 });
  }
  if (pieceCache.size > 64) pieceCache.clear();
  pieceCache.set(board, out);
  return out;
}

const prefixOf = (m: CMove, path: number[]) => m.path.length >= path.length && path.every((s, i) => m.path[i] === s);

function rules(g: CheckersState, seat: number): PickRules | null {
  if (g.result || g.turn !== seat) return null;
  const legal = legalOf(g.board, g.turn);
  return {
    key: `${g.board}${g.turn}`,
    starts: new Set(legal.map((m) => m.path[0])),
    next: (path) => [...new Set(legal.filter((m) => m.path.length > path.length && prefixOf(m, path)).map((m) => m.path[path.length]))],
    finish: (path) => (legal.some((m) => m.path.length === path.length && prefixOf(m, path)) ? { type: 'move', path } : null),
  };
}

export const checkersUi: BoardGameUI<CheckersState> = {
  options: [
    { key: 'level', label: 'Computer', values: [{ v: 'easy', label: 'Easy' }, { v: 'medium', label: 'Medium' }, { v: 'hard', label: 'Hard' }] },
  ],
  defaults: { level: 'medium' },
  pieces: (g) => piecesOf(g.board),
  last: (g) => g.last ?? [],
  check: () => null,
  turn: (g) => (g.result ? null : g.turn),
  rules,
  moves: (g) => g.moves,
  result: (g) => (g.result ? { winner: g.result.winner, text: resultText(g) } : null),
  drawOffer: (g) => g.drawOffer,
  glyph: (p) => <span className={`gx-bd-disc side-${p.side} ${p.kind === 'king' ? 'is-king' : ''}`}>{p.kind === 'king' ? '♛︎' : ''}</span>,
  Piece: CheckerPiece,
};

export { SEAT_NAMES };

export const CHECKERS_TUTORIAL = { url: 'https://www.youtube.com/watch?v=1eVZV3TzXUI', title: 'How to play Checkers — CardGames.io' };

export const CHECKERS_RULES: { title: string; lines: string[] }[] = [
  {
    title: 'Setup',
    lines: [
      'American checkers (English draughts): 8×8 board, 12 pieces each on the dark squares. Black moves first.',
      'Tap one of your pieces, then a highlighted square. In VR, poke or pinch the piece on the table, then the square.',
    ],
  },
  {
    title: 'Moving',
    lines: [
      'Men move one square diagonally forward. Kings move one square diagonally forward or back.',
      'Jump an opponent\'s piece by hopping over it diagonally onto the empty square beyond — it\'s captured.',
    ],
  },
  {
    title: 'Captures',
    lines: [
      'Captures are mandatory: if you can jump, you must.',
      'After a jump, keep jumping with the same piece while you can — tap each landing square in turn. You may pick any jumping route.',
    ],
  },
  {
    title: 'Kings',
    lines: ['A man reaching the far row is crowned (two discs stacked). Crowning ends the move, even mid-jump.'],
  },
  {
    title: 'Winning and draws',
    lines: [
      'You win when your opponent can\'t move — no pieces left, or every piece blocked.',
      'Draw by agreement, or after 40 moves each with no capture and no uncrowned piece moving.',
      'Resign any time. Going 10 minutes without moving forfeits.',
    ],
  },
];
